import {
  CheckboxInputComponent,
  HeightInputComponent,
  INPUT_COMPONENT_TYPE,
  NumberOrDateInputComponent,
  STEP_TYPE,
  TextareaInputComponent,
  TextComponent,
  TextInputComponent,
  WeightInputComponent
} from '@abiglobalhealth/go-types'
import { t } from 'i18next'
import { useEffect, useMemo, useState } from 'react'

import { Checkbox } from '@/components/CheckboxGroup/Checkbox'
import { UserFormData } from '@/contexts/userForm'
import { useCheckBoxModal } from '@/hooks/useCheckBoxModal'
import { useFormErrors } from '@/hooks/useFormErrors'
import { useIndications } from '@/hooks/useIndications'
import { useLoading } from '@/hooks/useLoading'
import { useQuestion } from '@/hooks/useQuestion'
import { useStep } from '@/hooks/useStep'
import { useUserForm } from '@/hooks/useUserForm'
import i18n from '@/locale/i18n'
import { isRTL } from '@/locale/utils'
import { cn } from '@/utils/tailwind'

import { getZodSchema } from './validator'

const KG_LBS_CONVERSION_RATE = 2.20462

export enum WEIGHT_UNIT {
  KG = 'kg',
  ST_LB = 'st/lb'
}
export enum HEIGHT_UNIT {
  CM = 'cm',
  FT_IN = 'ft-in'
}

export type InputProps = (
  | NumberOrDateInputComponent
  | TextInputComponent
  | CheckboxInputComponent
  | TextareaInputComponent
  | WeightInputComponent
  | HeightInputComponent
) & {
  value?: string | number | boolean
  tabIndex: number
  isInRow?: boolean
  customTextareaClassName?: string
  textAreaInnerError?: boolean
  className?: string
  disabled?: boolean
  onChange?: (value: UserFormData) => void
  hideError?: boolean
}

export const Input = (props: InputProps) => {
  const { id, textKey, required, type, isInRow, onChange, hideError, tabIndex, textAreaInnerError, customTextareaClassName, value, disabled } = props

  const { addData, getUserDataById } = useUserForm()
  const { currentStep } = useStep()
  const { open } = useCheckBoxModal()
  const { updateQuestion, question } = useQuestion()
  const { indications, addIndication } = useIndications()

  const { clearFormError, addFormError, getFieldError, formErrors } = useFormErrors()

  const { showLoading } = useLoading()

  const isWeight = type === INPUT_COMPONENT_TYPE.WEIGHT

  const getInitialValue = () => {
    if (id === 'question') return question.question

    switch (currentStep?.type) {
      case STEP_TYPE.USER_FORM: {
        const currentData = getUserDataById(id)
        if (id === 'dateOfBirth') return (currentData as string)?.split('T')[0]

        const weightUnit = getUserDataById('weightUnit') as WEIGHT_UNIT
        if (isWeight && weightUnit === WEIGHT_UNIT.ST_LB) return (Number(currentData) * KG_LBS_CONVERSION_RATE).toFixed(2)

        return currentData
      }

      case STEP_TYPE.INDICATION_SURVEY: {
        return indications?.find((indication) => indication.id === currentStep!.id)?.answers.find((answer) => answer.id === id)?.value
      }

      default: {
        return value ?? null
      }
    }
  }

  const [inputValue, setInputValue] = useState<UserFormData | null>(getInitialValue())
  const textareaValue = id === 'question' ? (question.question ?? '') : typeof inputValue === 'string' ? inputValue : ''
  const [weightUnit, setWeightUnit] = useState<WEIGHT_UNIT>(() => (getUserDataById('weightUnit') as WEIGHT_UNIT) || WEIGHT_UNIT.KG)
  const [heightUnit, setHeightUnit] = useState<HEIGHT_UNIT>(() => (getUserDataById('heightUnit') as HEIGHT_UNIT) || HEIGHT_UNIT.CM)

  const isNumber = [INPUT_COMPONENT_TYPE.NUMBER, INPUT_COMPONENT_TYPE.WEIGHT].includes(type)

  let minLength: number | undefined
  let maxLength: number | undefined
  let min: number | undefined
  let max: number | undefined
  let placeholder: string | undefined
  let checked: boolean | undefined
  let isDecimal: boolean | undefined
  let minDate: string | undefined
  let maxDate: string | undefined

  switch (type) {
    case INPUT_COMPONENT_TYPE.TEXT:
    case INPUT_COMPONENT_TYPE.EMAIL:
    case INPUT_COMPONENT_TYPE.TEXTAREA:
      minLength = props.minLength
      maxLength = props.maxLength
      placeholder = props.placeholder
      break

    case INPUT_COMPONENT_TYPE.NUMBER:
      placeholder = props.placeholder
      isDecimal = props.isDecimal
      min = props.min
      max = props.max
      break

    case INPUT_COMPONENT_TYPE.DATE:
      placeholder = props.placeholder
      minDate = props.minDate
      maxDate = props.maxDate
      break

    case INPUT_COMPONENT_TYPE.CHECKBOX:
      checked = props.checked
      break

    case INPUT_COMPONENT_TYPE.WEIGHT:
    case INPUT_COMPONENT_TYPE.HEIGHT:
      placeholder = props.placeholder
      min = 0
      break
  }

  const schema = useMemo(
    () =>
      getZodSchema({
        type,
        id,
        minLength,
        maxLength,
        min,
        max,
        required,
        minDate,
        maxDate
      }),
    [type, minLength, maxLength, min, max, required, i18n.language, minDate, maxDate]
  )

  const handleOnBlurValidation = (validateValue: string | boolean) => {
    const validated = schema.safeParse(isNumber ? Number(validateValue ?? 0) : validateValue)

    if (!validated.success) addFormError(id, validated.error.issues[0].message)
  }

  useEffect(() => {
    if (inputValue === null) return

    if (onChange) {
      return onChange(inputValue)
    }

    if (id === 'question') {
      return updateQuestion(inputValue as string)
    }

    switch (currentStep?.type) {
      case STEP_TYPE.USER_FORM:
        if (isWeight) {
          const valueInKg = Number((weightUnit === WEIGHT_UNIT.KG ? Number(inputValue) : Number(inputValue) / KG_LBS_CONVERSION_RATE).toFixed(2))
          addData(id, valueInKg)
          addData('weightUnit', weightUnit)
          return
        }

        if (isNumber) return addData(id, Number(inputValue))

        return addData(id, inputValue)

      case STEP_TYPE.INDICATION_SURVEY: {
        const question = currentStep!.components[currentStep!.componentsOrder[0]] as TextComponent
        addIndication({
          id: currentStep!.id,
          questionKey: question.textKey,
          question: t(question.textKey),
          answers: [
            {
              id,
              key: textKey!,
              value: inputValue as string
            }
          ]
        })
        break
      }
    }
  }, [inputValue])

  useEffect(() => {
    if (!isWeight || !inputValue) return

    // Convert current value to the new unit
    const prevWeightUnit = getUserDataById('weightUnit') as WEIGHT_UNIT
    if (prevWeightUnit === weightUnit) return

    setInputValue((prev) => {
      const prevNumber = Number(prev)
      return weightUnit === WEIGHT_UNIT.KG ? (prevNumber / KG_LBS_CONVERSION_RATE).toFixed(0) : (prevNumber * KG_LBS_CONVERSION_RATE).toFixed(2)
    })
  }, [weightUnit])

  const error = getFieldError(id)

  // Delegate clicks on links inside the checkbox label (rendered via
  // dangerouslySetInnerHTML) to open them in the modal. The listener is attached
  // to the stable label container rather than the <a>, which React can recreate
  // on re-render, so the handler keeps working across renders.
  useEffect(() => {
    const checkboxLabelElement = document.getElementById(`${id}-checkbox-label`)
    if (!checkboxLabelElement) return

    const handleLabelLinkClick = (ev: MouseEvent) => {
      const anchor = (ev.target as HTMLElement | null)?.closest('a')
      if (!anchor) return

      ev.preventDefault()
      window.scrollTo(0, 0)
      // Open the modal with the url
      open(anchor.href)
      showLoading()
    }

    checkboxLabelElement.addEventListener('click', handleLabelLinkClick)
    return () => checkboxLabelElement.removeEventListener('click', handleLabelLinkClick)
  }, [id, open, showLoading])

  useEffect(() => {
    const currentData = getUserDataById(id)

    // On every language change, we update the error message
    if (!formErrors || !currentData) return

    const currentError = formErrors[id]
    if (!currentError) return // No error to update translation

    const validated = schema.safeParse(isNumber ? Number(currentData ?? 0) : currentData)

    if (!validated.success) addFormError(id, validated.error.issues[0].message)
  }, [i18n.language])

  useEffect(() => {
    if (value === undefined) return
    setInputValue(value)
  }, [value])

  const isDate = type === INPUT_COMPONENT_TYPE.DATE
  const dateOrNumber = isDate || isNumber

  const customClasses = {
    container: dateOrNumber && !isInRow ? '' : 'w-full',
    input: dateOrNumber && !isInRow ? `${isDate ? 'w-40' : 'w-20'} text-center` : 'min-w-[4rem] w-full'
  }

  switch (type) {
    case INPUT_COMPONENT_TYPE.CHECKBOX:
      return (
        <Checkbox
          id={id}
          textKey={textKey}
          required={required}
          checked={Boolean(inputValue)}
          tabIndex={tabIndex}
          onBlur={(ev) => {
            handleOnBlurValidation(ev.target.checked)
          }}
          onChange={(ev) => {
            clearFormError(id)
            setInputValue(ev.target.checked)
          }}
        />
      )

    case INPUT_COMPONENT_TYPE.TEXTAREA:
      return (
        <div className="relative flex flex-1 flex-col py-1">
          {textKey && (
            <label htmlFor={id} className="font-bold">
              {t(textKey)} {required && '*'}
            </label>
          )}

          <textarea
            id={id}
            name={id}
            tabIndex={tabIndex}
            required={required}
            disabled={disabled}
            className={cn(
              'flex-1 rounded-lg border bg-white p-4',
              error ? 'border-error' : 'border-gray focus-visible:border-primary focus-visible:outline-none',
              'min-h-24 w-full resize-none transition-[border-color]',
              disabled ? 'animate-pulse cursor-not-allowed opacity-70' : '',
              customTextareaClassName
            )}
            placeholder={placeholder ? (t(placeholder) ?? '') : ''}
            minLength={minLength}
            maxLength={maxLength}
            value={textareaValue}
            onChange={(ev) => {
              clearFormError(id)
              if (id === 'question') updateQuestion(ev.target.value)
              else setInputValue(ev.target.value)
            }}
            onBlur={(ev) => {
              handleOnBlurValidation(ev.target.value)
            }}
            onFocus={() => clearFormError(id)}
          />

          <div className={`${textAreaInnerError ? 'absolute bottom-2 transform justify-center' : 'min-h-6'} flex w-full`}>
            {minLength && (!textareaValue || textareaValue.length < minLength) ? (
              <span className={`${error ? 'text-error' : 'opacity-20'} transition-all`}>
                {error && !textareaValue.length ? t('inputs.textarea.question_empty') : t('inputs.textarea.min_length', { minLength })}
              </span>
            ) : error && !hideError ? (
              <span className="text-error text-sm" data-testid="error-span">
                {error && t(error)}
              </span>
            ) : null}
          </div>
        </div>
      )

    case INPUT_COMPONENT_TYPE.HEIGHT: {
      const isCm = heightUnit === HEIGHT_UNIT.CM
      const inputId = isCm ? 'height-cm' : 'height-ft'

      return (
        <div className="flex flex-col py-1">
          {textKey && (
            <label htmlFor={id} className="font-bold">
              {t(textKey)} {required && '*'}
            </label>
          )}

          <div className="flex">
            <div
              className={cn(
                'flex h-14.5 items-center gap-1 rounded-tl-lg rounded-bl-lg border bg-white p-2',
                error ? 'border-error' : 'border-gray focus-within:border-primary'
              )}
            >
              <input
                className={cn('h-full text-center', isCm ? 'w-full' : 'w-1/2')}
                placeholder={isCm ? 'cm' : 'ft'}
                name={inputId}
                type="number"
                onFocus={() => clearFormError(inputId)}
                onChange={() => {
                  clearFormError(inputId)
                }}
                min={min}
              />
              {!isCm && (
                <>
                  <span>-</span>
                  <input
                    className="h-full w-1/2 text-center"
                    placeholder="in"
                    name="height-in"
                    type="number"
                    onFocus={() => clearFormError('height-in')}
                    onChange={() => {
                      clearFormError('height-in')
                    }}
                  />
                </>
              )}
            </div>

            <select
              data-testid="height-unit-select"
              className={cn(
                'min-w-18 border px-3',
                error ? 'border-error' : 'border-gray focus-visible:border-primary focus-visible:outline-none',
                isRTL() ? 'rounded-tl-lg rounded-bl-lg border-r-0' : 'rounded-tr-lg rounded-br-lg border-l-0'
              )}
              style={{
                backgroundPositionX: isRTL() ? '20%' : '80%'
              }}
              value={heightUnit}
              onChange={(ev) => setHeightUnit(ev.target.value as HEIGHT_UNIT)}
              name="height-unit"
            >
              {Object.values(HEIGHT_UNIT).map((unit) => (
                <option key={unit} value={unit}>
                  {unit}
                </option>
              ))}
            </select>
          </div>

          {!hideError && (
            <span className="text-error min-h-6 text-sm" data-testid="error-span">
              {error && t(error)}
            </span>
          )}
        </div>
      )
    }

    default: {
      return (
        <div className={`flex flex-col py-1 ${customClasses.container}`}>
          {textKey && (
            <label htmlFor={id} className="font-bold">
              {t(textKey)} {required && '*'}
            </label>
          )}

          <div className="flex">
            <input
              id={id}
              name={id}
              tabIndex={tabIndex}
              type={isWeight ? INPUT_COMPONENT_TYPE.NUMBER : type}
              required={required}
              className={cn(
                isWeight ? (isRTL() ? 'rounded-tr-lg rounded-br-lg' : 'rounded-tl-lg rounded-bl-lg') : 'rounded-lg',
                'border p-4',
                error ? 'border-error' : 'border-gray focus-visible:border-primary focus-visible:outline-none',
                customClasses.input,
                dateOrNumber ? 'text-center' : '',
                isInRow && 'grow'
              )}
              onFocus={() => clearFormError(id)}
              onBlur={(ev) => {
                const val = ev.target.value.trim()
                setInputValue(val)
                handleOnBlurValidation(val)
              }}
              style={{
                width: isWeight && inputValue ? `${inputValue.toString().length + 7}ch` : undefined
              }}
              placeholder={placeholder ? (t(placeholder) ?? '') : ''}
              minLength={minLength}
              maxLength={maxLength}
              checked={checked}
              min={isNumber ? min : isDate ? minDate : undefined}
              max={isNumber ? max : isDate ? maxDate : undefined}
              step={isWeight ? '0.01' : isNumber ? (isDecimal ? 'any' : '1') : undefined}
              value={typeof inputValue === 'string' ? inputValue : typeof inputValue === 'number' ? inputValue.toString() : ''}
              onChange={(ev) => {
                clearFormError(id)
                setInputValue(ev.target.value)
              }}
              onKeyDown={(evt) => {
                // Only accept numbers, dot, comma and return if the input is a number
                if (isNumber && !['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab'].includes(evt.key) && !/[\d.,]/.test(evt.key)) {
                  evt.preventDefault()
                }
              }}
            />

            {isWeight && (
              <select
                data-testid="weight-unit-select"
                className={cn(
                  'min-w-18 border px-4 shadow-md',
                  error ? 'border-error' : 'border-gray focus-visible:border-primary focus-visible:outline-none',
                  isRTL() ? 'rounded-tl-lg rounded-bl-lg border-r-0' : 'rounded-tr-lg rounded-br-lg border-l-0'
                )}
                style={{
                  backgroundPositionX: isRTL() ? '20%' : '80%'
                }}
                value={weightUnit}
                onChange={(ev) => setWeightUnit(ev.target.value as WEIGHT_UNIT)}
                name="weight-unit"
              >
                {Object.values(WEIGHT_UNIT).map((unit) => (
                  <option key={unit} value={unit}>
                    {unit === WEIGHT_UNIT.ST_LB ? 'lbs' : unit}
                  </option>
                ))}
              </select>
            )}
          </div>

          {!hideError && (
            <span className="text-error min-h-6 text-sm" data-testid="error-span">
              {error && t(error)}
            </span>
          )}
        </div>
      )
    }
  }
}
